import AuthAPI from '@/api/auth';
import { persistentStorage, PersistentStorageKeys } from '@/utils/persistentStorage';
import { parseJwt } from '@/utils/jwt';
import _ from 'lodash';
import { Nullable } from '@/types/utility';
import { apiHasura } from '@/api/hasura';

const MS_IN_SECOND = 1000;
const FIVE_SECONDS = 5 * MS_IN_SECOND;

function isTokenOld(token: string, expiry: number): boolean {
  if (token && expiry) {
    const now = new Date();
    return expiry - FIVE_SECONDS < now.getTime();
  }
  return true;
}

const authServiceListeners: ((token: string, data: any) => void)[] = [];

export class AuthService {
  public static requestRefreshToken: Nullable<Promise<any>>;

  public static login(email, pass, onSuccess, onError) {
    return AuthAPI.login(
      email,
      pass,
      (data) => {
        this.saveRefreshToken(data.refresh);
        this.saveToken(data.access);
        this.saveExpiry(new Date().getTime() + data.expires_in * MS_IN_SECOND);
        onSuccess(data);
      },
      onError,
    );
  }

  public static googleLogin(idToken, onSuccess, onError) {
    return AuthAPI.googleLogin(
      idToken,
      (data) => {
        this.saveRefreshToken(data.refresh);
        this.saveToken(data.access);
        this.saveExpiry(new Date().getTime() + data.expires_in * MS_IN_SECOND);
        onSuccess(data);
      },
      onError,
    );
  }

  public static isAuthenticated() {
    return this.getToken() != null;
  }

  public static userData() {
    return parseJwt(this.getToken()) ?? {};
  }

  public static token() {
    return this.getToken();
  }

  public static tokenRefreshedIfOld() {
    const token = this.getToken();

    if (token && this.expiry) {
      const isOld = isTokenOld(token, this.expiry);

      if (isOld) {
        return this.refreshAccessToken();
      }
      return token;
    }

    return null;
  }

  public static async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();

    AuthService.requestRefreshToken =
      AuthService.requestRefreshToken ?? AuthAPI.refreshToken(refreshToken, null, null);

    try {
      const { ok, data } = (await AuthService.requestRefreshToken) || {};

      if (ok && data.access) {
        const { access, refresh, expires_in } = data;

        refresh && this.saveRefreshToken(refresh);
        this.saveToken(access);
        this.saveExpiry(new Date().getTime() + expires_in * MS_IN_SECOND);

        return access;
      }
      this.saveToken(undefined);

      return null;
    } finally {
      AuthService.requestRefreshToken = null;
    }
  }

  public static async logout() {
    await AuthAPI.logout(null);
    this.saveRefreshToken(null);
    this.saveToken(null);
    this.saveExpiry(null);
  }

  public static addListener(l) {
    authServiceListeners.push(l);
  }

  public static removeListener(l) {
    _.pull(authServiceListeners, l);
  }

  // Private

  private static getToken() {
    return persistentStorage.get(PersistentStorageKeys.ACCESS_TOKEN_KEY, {
      isForActiveUser: false,
    });
  }

  private static saveToken(token) {
    persistentStorage.set(PersistentStorageKeys.ACCESS_TOKEN_KEY, token, {
      isForActiveUser: false,
    });
    this.broadcast(token);
  }

  private static getRefreshToken() {
    return persistentStorage.get(PersistentStorageKeys.REFRESH_TOKEN_KEY, {
      isForActiveUser: false,
    });
  }

  private static saveRefreshToken(token) {
    persistentStorage.set(PersistentStorageKeys.REFRESH_TOKEN_KEY, token, {
      isForActiveUser: false,
    });
  }

  private static saveExpiry(expiry) {
    persistentStorage.set(PersistentStorageKeys.EXPIRY, expiry, { isForActiveUser: false });
  }

  private static get expiry() {
    return persistentStorage.get(PersistentStorageKeys.EXPIRY, { isForActiveUser: false });
  }

  private static broadcast(token) {
    authServiceListeners.forEach((l) => l(token, parseJwt(token)));
  }

  public static async getHasuraAnonymousToken(shareToken, objectId, doForceTokenRefresh = false) {
    const tokens =
      persistentStorage.get(PersistentStorageKeys.ANONYMOUS_TOKENS, {
        isForActiveUser: false,
      }) ?? {};

    const token = tokens[shareToken];

    // If doForceTokenRefresh is true, skip token validation and always fetch a new token
    if (!doForceTokenRefresh && token) {
      const data = parseJwt(token);

      if (!isTokenOld(token, data.exp * MS_IN_SECOND)) {
        return token;
      }
    }

    const jwtTokenForHasura: string = await apiHasura.getAnonymousToken(shareToken, objectId);

    tokens[shareToken] = jwtTokenForHasura;

    persistentStorage.set(PersistentStorageKeys.ANONYMOUS_TOKENS, tokens, {
      isForActiveUser: false,
    });

    return jwtTokenForHasura;
  }
}
